import { RacemapColors } from '@racemap/utilities/consts/common';
import type { AggregationColor } from 'antd/es/color-picker/color';
import { useSyncExternalStore } from 'react';
import tinycolor from 'tinycolor2';
import { markerColorToString } from '../colorUtils';

const STORAGE_KEY = 'racemap-recent-colors';
const MAX_RECENT_COLORS = 12;

// A handful of distinct default colors, shown until the user has picked any of their own.
export const DEFAULT_COLORS: Array<string> = [
  RacemapColors.BaseRed,
  RacemapColors.BaseGreen,
  RacemapColors.PaleBlue,
  RacemapColors.Orange,
  RacemapColors.ChartYellow,
  RacemapColors.CloudBlue,
  RacemapColors.DarkDarkGray,
  RacemapColors.BaseYellow,
];

/**
 * Prepends `color` to `colors`, deduping case-insensitively and capping the length.
 * Exported standalone so the list logic is unit-testable without touching localStorage.
 */
export function addRecentColor(colors: Array<string>, color: string): Array<string> {
  const withoutDuplicate = colors.filter((c) => c.toLowerCase() !== color.toLowerCase());
  return [color, ...withoutDuplicate].slice(0, MAX_RECENT_COLORS);
}

function readColors(): Array<string> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw != null ? JSON.parse(raw) : null;
    if (!Array.isArray(parsed)) throw new Error('Expected an array');
    if (parsed.length === 0) throw new Error('Expected a non-empty array');
    if (!parsed.every((c) => typeof c === 'string'))
      throw new Error('Expected an array of strings');
    const colors = parsed.map((c) => tinycolor(c));
    if (!colors.every((c) => c.isValid())) throw new Error('Expected all valid colors');

    return colors.map((c) => c.toHexString());
  } catch {
    return DEFAULT_COLORS;
  }
}

// Module-level cache + pub/sub, so every hook instance across the app shares one list
// (updating one color picker's history immediately shows up in every other picker).
let cachedColors = readColors();
const listeners = new Set<() => void>();

function setColors(colors: Array<string>) {
  cachedColors = colors;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(colors));
  } catch {
    // ponytail: localStorage can throw (private mode, quota) — the in-memory list still works for this session.
  }
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * Shared, browser-local (not saved to the user object) list of recently picked colors,
 * used to populate the `presets` of every `ColorPicker` in the app.
 */
export function useRecentColors(): {
  presets: Array<{ label: string; colors: Array<string> }>;
  rememberColor: (color: AggregationColor | null | undefined) => void;
} {
  const colors = useSyncExternalStore(subscribe, () => cachedColors);

  const rememberColor = (color: AggregationColor | null | undefined) => {
    if (color == null) return;
    setColors(addRecentColor(cachedColors, markerColorToString(color)));
  };

  return {
    presets: [{ label: 'Recent', colors }],
    rememberColor,
  };
}
